import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { adaptAlerts } from "./monitoringAdapters";
import {
	acknowledgeMonitoringAlert,
	getMonitoringAlerts,
} from "../../../services/monitoringApi";

function AlertDetailBody({ detail }) {
	if (!detail) return null;

	if (detail.kind === "text") {
		return <p className="monitoring__alert-detail mb-0">{detail.text}</p>;
	}

	if (detail.kind === "prompts") {
		return (
			<ul className="monitoring__alert-prompt-list list-unstyled mb-0">
				{detail.prompts.map((prompt) => (
					<li key={prompt.id ?? prompt.text} className="monitoring__alert-prompt-item">
						{prompt.text ? (
							<span className="monitoring__alert-prompt-text">{prompt.text}</span>
						) : null}
						{/* {prompt.id != null ? (
							<span className="monitoring__alert-prompt-id">Prompt #{prompt.id}</span>
						) : null} */}
					</li>
				))}
			</ul>
		);
	}

	if (detail.kind === "metrics" || detail.kind === "kv") {
		return (
			<dl className="monitoring__alert-metrics mb-0">
				{detail.items.map((item) => (
					<div key={item.label} className="monitoring__alert-metric-row">
						<dt>{item.label}</dt>
						<dd>{item.value}</dd>
					</div>
				))}
			</dl>
		);
	}

	return null;
}

export default function MonitoringAlerts({ websiteId }) {
	const [feed, setFeed] = useState([]);
	const [rules, setRules] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [ackBusy, setAckBusy] = useState(null);

	const load = useCallback(async () => {
		if (!websiteId) {
			setLoading(false);
			setError("Missing website");
			return;
		}
		setLoading(true);
		setError(null);
		const res = await getMonitoringAlerts(websiteId);
		if (!res.ok) {
			setError(res.message || "Failed to load alerts");
			setFeed([]);
			setRules([]);
			setLoading(false);
			return;
		}
		const adapted = adaptAlerts({ alerts: res.alerts, rules: res.rules });
		setFeed(adapted.feed);
		setRules(adapted.rules);
		setLoading(false);
	}, [websiteId]);

	useEffect(() => {
		load();
	}, [load]);

	async function acknowledge(id) {
		if (!id || ackBusy) return;
		setAckBusy(id);
		const res = await acknowledgeMonitoringAlert(websiteId, id);
		setAckBusy(null);
		if (!res.ok) {
			toast.error(res.message || "Could not acknowledge alert");
			return;
		}
		setFeed((prev) =>
			prev.map((item) => (item.id === id ? { ...item, status: "resolved" } : item))
		);
	}

	if (loading) {
		return <p className="monitoring__empty text-muted mb-0">Loading alerts…</p>;
	}
	if (error) {
		return <p className="monitoring__empty monitoring__empty--error mb-0">{error}</p>;
	}

	return (
		<div className="monitoring__alerts">
			<div className="monitoring__alerts-grid">
				<section className="monitoring__panel monitoring__panel--feed">
					<h3 className="monitoring__panel-title">Alert feed</h3>
					{feed.length === 0 ? (
						<p className="monitoring__empty text-muted mb-0">
							No alerts yet — rule hits from the change journal will appear here.
						</p>
					) : (
						<ul className="monitoring__alert-feed list-unstyled mb-0">
							{feed.map((item) => (
								<li
									key={item.id}
									className={`monitoring__alert-row monitoring__alert-row--${item.severity}`}
								>
									<span
										className="monitoring__alert-marker"
										aria-hidden="true"
									/>
									<div className="monitoring__alert-row-body">
										<p className="monitoring__alert-headline mb-0">
											<span className="monitoring__alert-title">
												{item.title}
											</span>
											{item.timestamp ? (
												<>
													<span className="monitoring__alert-sep" aria-hidden>
														{" "}
														·{" "}
													</span>
													<span className="monitoring__alert-time">
														{item.timestamp}
													</span>
												</>
											) : null}
										</p>
										<AlertDetailBody detail={item.detail} />
									</div>
									<div className="monitoring__alert-row-action">
										{item.status === "resolved" ? (
											<span className="monitoring__resolved-badge">
												Acknowledged
											</span>
										) : (
											<button
												type="button"
												className="monitoring__ack-btn"
												disabled={ackBusy === item.id}
												onClick={() => acknowledge(item.id)}
											>
												{ackBusy === item.id ? "…" : "Acknowledge"}
											</button>
										)}
									</div>
								</li>
							))}
						</ul>
					)}
				</section>

				<section className="monitoring__panel monitoring__panel--rules">
					<h3 className="monitoring__panel-title">
						Alert rules{" "}
						<span className="monitoring__panel-title-muted">(per-project settings)</span>
					</h3>
					{rules.length === 0 ? (
						<p className="monitoring__empty text-muted mb-0">No rules configured.</p>
					) : (
						<ul className="monitoring__rules-list list-unstyled mb-0">
							{rules.map((rule) => (
								<li key={rule.id} className="monitoring__rule-row">
									<span
										role="img"
										aria-label={rule.enabled ? "Rule enabled" : "Rule disabled"}
										className={`monitoring__toggle monitoring__toggle--readonly${
											rule.enabled ? " monitoring__toggle--on" : ""
										}`}
									>
										<span className="monitoring__toggle-knob" aria-hidden="true" />
									</span>
									<div className="monitoring__rule-text">
										<span className="monitoring__rule-label">{rule.label}</span>
										<span className="monitoring__rule-meta text-muted">
											{rule.meta}
										</span>
									</div>
								</li>
							))}
						</ul>
					)}
					<p className="monitoring__table-footnote mb-0 mt-3">
						Rule editing and channel delivery ship in Phase 4 — alerts are recorded in
						this feed now.
					</p>
				</section>
			</div>
		</div>
	);
}
